import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, RefreshCw, ShieldCheck, ArrowRightLeft, RotateCcw, Building2 } from 'lucide-react';

interface PlatformSubmissionRow {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  submissionId: string;
  invoiceId: string;
  invoiceNumber: string;
  standard: string;
  invoiceStatus: string;
  gateway: string;
  responseStatus: string;
  responseCode?: string | null;
  trackingId?: string | null;
  retryCount: number;
  lastError?: string | null;
  submittedAt?: string | null;
  acceptedAt?: string | null;
  rejectedAt?: string | null;
}

interface PlatformSubmissionResponse {
  items: PlatformSubmissionRow[];
  summary: {
    tenants: number;
    total: number;
    byGateway: Record<string, number>;
    byStatus: Record<string, number>;
  };
}

interface PlatformBulkActionResponse {
  tenantCount: number;
  processed: number;
  results: Array<{
    tenantId: string;
    tenantName: string;
    tenantSlug: string;
    processed: number;
    errors: Array<Record<string, unknown>>;
  }>;
}

interface PlatformSubmissionDetailResponse {
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  submission: Record<string, unknown>;
  invoice: Record<string, unknown> | null;
  parsedResponseBody: unknown;
  parsedData: unknown;
}

interface RunHistoryItem {
  id: string;
  run_id: string;
  tenant_id: string;
  goal_id: string;
  step_id: string;
  decision: string;
  tool_call: string;
  output_summary: string;
  error_code: string;
  error_message: string;
  level: string;
  created_at: string;
}

interface RunHistoryResponse {
  items: RunHistoryItem[];
  count: number;
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatJson(value: unknown) {
  if (value == null) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function inferCountry(row: PlatformSubmissionRow): 'SG' | 'MY' | 'OTHER' {
  if (row.gateway === 'INVOICENOW') return 'SG';
  if (row.gateway === 'MYINVOIS_API') return 'MY';
  return 'OTHER';
}

function buildWorkflowSteps(row: PlatformSubmissionRow) {
  const status = (row.responseStatus || '').toLowerCase();
  const accepted = Boolean(row.acceptedAt) || status === 'accepted';
  const rejected = Boolean(row.rejectedAt) || status === 'rejected';
  const received = ['received', 'validated', 'accepted', 'rejected'].includes(status);
  const validated = ['validated', 'accepted', 'rejected'].includes(status);
  return [
    { key: 'submitted', label: 'Submitted', state: row.submittedAt ? 'done' : 'pending' },
    { key: 'received', label: 'Received', state: received ? 'done' : status === 'submitted' || status === 'pending' ? 'current' : 'pending' },
    { key: 'validated', label: 'Validated', state: validated ? 'done' : received ? 'current' : 'pending' },
    { key: 'accepted', label: 'Accepted', state: accepted ? 'done' : rejected ? 'pending' : validated ? 'current' : 'pending' },
    { key: 'rejected', label: 'Rejected', state: rejected ? 'done' : 'pending' },
  ];
}

export default function EInvoicingOpsPage() {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<'sync' | 'retry' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [country, setCountry] = useState<'all' | 'SG' | 'MY'>('all');
  const [gateway, setGateway] = useState('all');
  const [status, setStatus] = useState('all');
  const [tenantId, setTenantId] = useState('');
  const [data, setData] = useState<PlatformSubmissionResponse | null>(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<PlatformSubmissionDetailResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [runHistory, setRunHistory] = useState<RunHistoryItem[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (gateway !== 'all') params.set('gateway', gateway);
      if (status !== 'all') params.set('status', status);
      if (tenantId.trim()) params.set('tenantId', tenantId.trim());
      const res = await api.get<PlatformSubmissionResponse>(`/platform/e-invoicing/submissions?${params.toString()}`);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load e-invoicing queue');
    } finally {
      setLoading(false);
    }
  }, [gateway, status, tenantId]);

  const loadRunHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (tenantId.trim()) params.set('tenantId', tenantId.trim());
      const res = await api.get<RunHistoryResponse>(`/platform/e-invoicing/run-history?${params.toString()}`);
      setRunHistory(res.items ?? []);
    } catch {
      setRunHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadRunHistory();
  }, [loadRunHistory]);

  useEffect(() => {
    setSelectedSubmissionId(null);
  }, [country, gateway, status, tenantId]);

  const filteredItems = useMemo(() => {
    const items = data?.items ?? [];
    if (country === 'all') return items;
    return items.filter((row) => inferCountry(row) === country);
  }, [country, data?.items]);

  const derivedSummary = useMemo(() => ({
    tenants: new Set(filteredItems.map((item) => item.tenantId)).size,
    total: filteredItems.length,
    byGateway: filteredItems.reduce<Record<string, number>>((acc, item) => {
      acc[item.gateway] = (acc[item.gateway] ?? 0) + 1;
      return acc;
    }, {}),
    byStatus: filteredItems.reduce<Record<string, number>>((acc, item) => {
      const key = item.responseStatus || 'unknown';
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {}),
  }), [filteredItems]);

  const statusKeys = useMemo(() => Object.keys(derivedSummary.byStatus).sort(), [derivedSummary.byStatus]);
  const gatewayKeys = useMemo(() => Object.keys(derivedSummary.byGateway).sort(), [derivedSummary.byGateway]);
  const selectedSubmission = useMemo(
    () => filteredItems.find((row) => row.submissionId === selectedSubmissionId) ?? filteredItems[0] ?? null,
    [filteredItems, selectedSubmissionId],
  );
  const tenantBreakdown = useMemo(() => {
    const grouped = new Map<string, { tenantId: string; tenantName: string; tenantSlug: string; count: number; accepted: number; errors: number }>();
    for (const item of filteredItems) {
      const existing = grouped.get(item.tenantId) ?? {
        tenantId: item.tenantId,
        tenantName: item.tenantName,
        tenantSlug: item.tenantSlug,
        count: 0,
        accepted: 0,
        errors: 0,
      };
      existing.count += 1;
      if ((item.responseStatus || '').toLowerCase() === 'accepted') existing.accepted += 1;
      if (item.lastError || ['failed', 'error', 'rejected'].includes((item.responseStatus || '').toLowerCase())) existing.errors += 1;
      grouped.set(item.tenantId, existing);
    }
    return [...grouped.values()].sort((a, b) => b.count - a.count);
  }, [filteredItems]);

  const runBulkAction = useCallback(async (action: 'sync' | 'retry') => {
    setActionLoading(action);
    setActionMessage(null);
    setError(null);
    try {
      const payload: Record<string, unknown> = { limit: 10 };
      if (tenantId.trim()) payload.tenantId = tenantId.trim();
      if (action === 'retry') payload.maxRetries = 3;
      const path = action === 'sync'
        ? '/platform/e-invoicing/sync-pending'
        : '/platform/e-invoicing/retry-failed';
      const res = await api.post<PlatformBulkActionResponse>(path, payload);
      setActionMessage(`${action === 'sync' ? 'Batch sync' : 'Batch retry'} 完成：${res.processed} submissions across ${res.tenantCount} tenants`);
      await load();
      await loadRunHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk action failed');
    } finally {
      setActionLoading(null);
    }
  }, [load, loadRunHistory, tenantId]);

  useEffect(() => {
    async function loadDetail() {
      if (!selectedSubmission) {
        setDetail(null);
        return;
      }
      setDetailLoading(true);
      try {
        const params = new URLSearchParams({ tenantId: selectedSubmission.tenantId });
        const res = await api.get<PlatformSubmissionDetailResponse>(
          `/platform/e-invoicing/submissions/${selectedSubmission.submissionId}?${params.toString()}`,
        );
        setDetail(res);
      } catch {
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    }
    void loadDetail();
  }, [selectedSubmission]);

  const exportCsv = useCallback(() => {
    const params = new URLSearchParams({ format: 'csv', limit: '500' });
    if (gateway !== 'all') params.set('gateway', gateway);
    if (status !== 'all') params.set('status', status);
    if (tenantId.trim()) params.set('tenantId', tenantId.trim());
    window.open(`/api/v1/platform/e-invoicing/submissions-export?${params.toString()}`, '_blank');
  }, [gateway, status, tenantId]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ShieldCheck className="h-6 w-6 text-primary" />
            E-Invoicing Ops
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Cross-tenant queue view for Singapore InvoiceNow and Malaysia MyInvois submissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => void runBulkAction('sync')} disabled={loading || actionLoading !== null}>
            <ArrowRightLeft className={`mr-2 h-4 w-4 ${actionLoading === 'sync' ? 'animate-spin' : ''}`} />
            Batch Sync Pending
          </Button>
          <Button variant="outline" onClick={() => void runBulkAction('retry')} disabled={loading || actionLoading !== null}>
            <RotateCcw className={`mr-2 h-4 w-4 ${actionLoading === 'retry' ? 'animate-spin' : ''}`} />
            Batch Retry Failed
          </Button>
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={loading}>
            Export CSV
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Tenant Count</div><div className="text-2xl font-semibold">{derivedSummary.tenants}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Visible Rows</div><div className="text-2xl font-semibold">{derivedSummary.total}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Gateways</div><div className="flex flex-wrap gap-1 pt-2">{gatewayKeys.map((key) => <Badge key={key} variant="outline">{key}:{derivedSummary.byGateway[key]}</Badge>)}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Statuses</div><div className="flex flex-wrap gap-1 pt-2">{statusKeys.map((key) => <Badge key={key} variant="outline">{key}:{derivedSummary.byStatus[key]}</Badge>)}</div></CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div className="space-y-1">
            <Label>Country View</Label>
            <Select value={country} onValueChange={(value: 'all' | 'SG' | 'MY') => setCountry(value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">all</SelectItem>
                <SelectItem value="SG">SG InvoiceNow</SelectItem>
                <SelectItem value="MY">MY MyInvois</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Gateway</Label>
            <Select value={gateway} onValueChange={setGateway}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">all</SelectItem>
                <SelectItem value="INVOICENOW">INVOICENOW</SelectItem>
                <SelectItem value="MYINVOIS_API">MYINVOIS_API</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">all</SelectItem>
                <SelectItem value="pending">pending</SelectItem>
                <SelectItem value="submitted">submitted</SelectItem>
                <SelectItem value="received">received</SelectItem>
                <SelectItem value="validated">validated</SelectItem>
                <SelectItem value="accepted">accepted</SelectItem>
                <SelectItem value="rejected">rejected</SelectItem>
                <SelectItem value="error">error</SelectItem>
                <SelectItem value="failed">failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Tenant ID</Label>
            <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="optional tenant UUID filter" />
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Load failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {actionMessage && (
        <Alert>
          <AlertTitle>Batch action completed</AlertTitle>
          <AlertDescription>{actionMessage}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.1fr_1.4fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tenant Drill-Down</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {tenantBreakdown.length ? tenantBreakdown.map((tenant) => (
              <button
                key={tenant.tenantId}
                type="button"
                onClick={() => setTenantId(tenant.tenantId)}
                className={`flex w-full items-start justify-between rounded-lg border px-3 py-3 text-left transition hover:bg-muted/40 ${tenantId === tenant.tenantId ? 'border-primary bg-primary/5' : ''}`}
              >
                <div>
                  <div className="flex items-center gap-2 font-medium">
                    <Building2 className="h-4 w-4 text-primary" />
                    {tenant.tenantName}
                  </div>
                  <div className="text-xs text-muted-foreground">{tenant.tenantSlug}</div>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <div>{tenant.count} submissions</div>
                  <div>{tenant.accepted} accepted</div>
                  <div>{tenant.errors} issue rows</div>
                </div>
              </button>
            )) : (
              <div className="text-sm text-muted-foreground">No tenant rows for current filter.</div>
            )}
            {tenantId && (
              <Button variant="ghost" size="sm" onClick={() => setTenantId('')}>Clear Tenant Filter</Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Selected Submission Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedSubmission ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{selectedSubmission.tenantSlug}</Badge>
                  <Badge variant="outline">{inferCountry(selectedSubmission)}</Badge>
                  <Badge variant="outline">{selectedSubmission.gateway}</Badge>
                  <Badge variant="outline">{selectedSubmission.responseStatus || 'unknown'}</Badge>
                </div>
                <div className="grid gap-2 md:grid-cols-5">
                  {buildWorkflowSteps(selectedSubmission).map((step) => (
                    <div
                      key={step.key}
                      className={`rounded-lg border px-3 py-3 text-sm ${
                        step.state === 'done'
                          ? 'border-green-300 bg-green-50 text-green-800'
                          : step.state === 'current'
                            ? 'border-blue-300 bg-blue-50 text-blue-800'
                            : 'border-muted bg-muted/20 text-muted-foreground'
                      }`}
                    >
                      <div className="font-medium">{step.label}</div>
                      <div className="mt-1 text-xs uppercase">{step.state}</div>
                    </div>
                  ))}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border p-3 text-sm">
                    <div className="font-medium">Invoice</div>
                    <div className="mt-2 text-muted-foreground">No: {selectedSubmission.invoiceNumber || '—'}</div>
                    <div className="text-muted-foreground">Standard: {selectedSubmission.standard || '—'}</div>
                    <div className="text-muted-foreground">Invoice Status: {selectedSubmission.invoiceStatus || '—'}</div>
                  </div>
                  <div className="rounded-lg border p-3 text-sm">
                    <div className="font-medium">Transport</div>
                    <div className="mt-2 text-muted-foreground">Tracking: {selectedSubmission.trackingId || '—'}</div>
                    <div className="text-muted-foreground">Response Code: {selectedSubmission.responseCode || '—'}</div>
                    <div className="text-muted-foreground">Retries: {selectedSubmission.retryCount}</div>
                  </div>
                </div>
                <div className="grid gap-2 md:grid-cols-3 text-sm text-muted-foreground">
                  <div>Submitted: {formatDateTime(selectedSubmission.submittedAt)}</div>
                  <div>Accepted: {formatDateTime(selectedSubmission.acceptedAt)}</div>
                  <div>Rejected: {formatDateTime(selectedSubmission.rejectedAt)}</div>
                </div>
                <div className="rounded-lg border p-3 text-sm">
                  <div className="font-medium">Last Error</div>
                  <div className="mt-2 text-muted-foreground whitespace-pre-wrap">{selectedSubmission.lastError || '—'}</div>
                </div>
                <div className="grid gap-3 xl:grid-cols-2">
                  <div className="rounded-lg border p-3 text-sm">
                    <div className="font-medium">Submission Raw Detail</div>
                    <pre className="mt-2 max-h-72 overflow-auto rounded bg-muted/30 p-3 text-xs">{detailLoading ? 'Loading...' : formatJson(detail?.submission)}</pre>
                  </div>
                  <div className="rounded-lg border p-3 text-sm">
                    <div className="font-medium">Transport Response / Data</div>
                    <pre className="mt-2 max-h-72 overflow-auto rounded bg-muted/30 p-3 text-xs">{detailLoading ? 'Loading...' : formatJson({ parsedResponseBody: detail?.parsedResponseBody, parsedData: detail?.parsedData, invoice: detail?.invoice })}</pre>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Select a submission row to inspect the workflow state.</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Submission Queue</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading queue...
            </div>
          ) : filteredItems.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Gateway</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Retries</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Last Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((row) => (
                  <TableRow
                    key={row.submissionId}
                    className={`cursor-pointer ${selectedSubmission?.submissionId === row.submissionId ? 'bg-primary/5' : ''}`}
                    onClick={() => setSelectedSubmissionId(row.submissionId)}
                  >
                    <TableCell>
                      <div className="font-medium">{row.tenantName}</div>
                      <div className="text-xs text-muted-foreground">{row.tenantSlug}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{row.invoiceNumber || '—'}</div>
                      <div className="text-xs text-muted-foreground">{row.standard} · {row.invoiceStatus}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{row.gateway}</Badge></TableCell>
                    <TableCell>
                      <div className="font-medium">{row.responseStatus || 'unknown'}</div>
                      <div className="text-xs text-muted-foreground">{row.responseCode || '—'}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.trackingId || '—'}</TableCell>
                    <TableCell>{row.retryCount}</TableCell>
                    <TableCell>{formatDateTime(row.submittedAt)}</TableCell>
                    <TableCell className="max-w-[260px] truncate text-xs text-muted-foreground">{row.lastError || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-sm text-muted-foreground">No submission rows for current filter.</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scheduler / Admin Run History</CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading run history...
            </div>
          ) : runHistory.length ? (
            <div className="space-y-3">
              {runHistory.map((item) => (
                <div key={item.id} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{item.tool_call}</Badge>
                    <Badge variant="outline">{item.level}</Badge>
                    <span className="text-xs text-muted-foreground">{formatDateTime(item.created_at)}</span>
                  </div>
                  <div className="mt-2 text-sm font-medium">{item.output_summary}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    tenant={item.tenant_id} · run={item.run_id} · decision={item.decision}
                  </div>
                  {item.error_code || item.error_message ? (
                    <div className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">
                      {item.error_code || 'NO_ERROR_CODE'} {item.error_message ? `· ${item.error_message}` : ''}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No scheduler/admin run history for current filter.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
